import { Injectable, ReflectiveInjector, Injector } from "@angular/core";
import {
    ITCIApplication,
    TCI_COMPONENT_MODE,
    TCI_APP_DETAIL_MENU_TYPE,
    APPLICATION_SCOPE_TYPES,
    USER_ROLES,
    TCITenantAppDetailsMenu,
    TCITenantSelectorType,
} from "@tci/common";
import { BaseAppDetailMenuComponentInput } from "../ng-app-details/ng-app-details-menu";
import { TciTenantModelService, AbstractTciTenant } from "../ng-tenant/tci-tenant.model";
import { TCITenantMenuServices } from "../ng-tenant/tci-tenant.menu";
import { NgAppFeatureToggleService } from "../ng-shared/ng-services/ng-app-feature-toggle.service";
import { NgUserService } from "../ng-shared/ng-services/ng-user.service";
import { TciTenantService } from "../ng-tenant/tci-tenant.service";
import { CanLoadComponentFunction } from "../ng-tenant/tci-tenant-component";
import { Observable, Subject } from "rxjs";

@Injectable()
export class NgAppHMService {

    // Status Menu artifacts
    statusMenuSubjects: { [key: string]: Subject<TCITenantAppDetailsMenu[]> } = {};

    constructor(
        private userSvc: NgUserService,
        private tenantSvc: TciTenantService,
        private tenantMenuSrv: TCITenantMenuServices,
        private tenantModelSvc: TciTenantModelService,
        private featureToggleSvc: NgAppFeatureToggleService,
        private injector: Injector
    ) { }

    // This subject will return the HM options on two cases:
    // 1. After calling this.getAppMenuOptions
    // 2. On appRefreshNotifSvc.getRefreshSubject notification coming from NgAppListService
    getAppMenuOptionsSubject(app: ITCIApplication): Subject<TCITenantAppDetailsMenu[]> {
        if (!this.statusMenuSubjects[app.id]) {
            this.statusMenuSubjects[app.id] = new Subject();
        }

        return this.statusMenuSubjects[app.id];
    }

    // Remove the unused HM subject
    removeHMSubject(app: ITCIApplication) {
        if (this.statusMenuSubjects[app.id]) {
            try {
                this.statusMenuSubjects[app.id].unsubscribe();
            } catch (e) { }
            delete this.statusMenuSubjects[app.id];
        }
    }

    // Collect HM options and send the result to the subject and also on demand
    getAppMenuOptions(app: ITCIApplication, mode: TCI_COMPONENT_MODE): Observable<TCITenantAppDetailsMenu[]> {
        // Before even starting checking the HM options we need to check the user role.
        // If the user is RO or the application is RO we don't have to check further.
        const subj = this.getAppMenuOptionsSubject(app);
        return this.tenantSvc.onInit()
            .flatMap(() => {
                const tenant = this.tenantModelSvc.getTenant(app.type);
                if (tenant) {
                    return this.checkIsRO(app) // User ADM and RO cases will be evaluated here
                        .flatMap(isRo => {

                            // Function to request the evaluation of the HM options
                            const getComponents = (component?: TCITenantSelectorType) => {
                                const input = this.getCanLoadFunctionInput(app, mode);
                                input.componentSelector = component;
                                return this.tenantMenuSrv
                                    .getAppDetailMenuItems(app.type, TCI_APP_DETAIL_MENU_TYPE.HAMBURGER_MENU, input)
                                    .map((comps: TCITenantAppDetailsMenu[]) => {
                                        subj.next(comps); // Notify the subscriber
                                        return comps;
                                    });
                            };

                            if (isRo) { // User not ADM and possibly RO
                                // IPAS-15997 - If user is not owner send "COPY" option for TCI apps
                                return this.userSvc.getUserInfo()
                                    .flatMap(usrInfo => {
                                        if (usrInfo.uname !== app.owner) {
                                            // We specify "copy" selector to avoid calling all the other HM "canLoad" functions
                                            return getComponents(TCITenantSelectorType.TCI_APP_DETAIL_COPY_APPLICATION);
                                        }

                                        // Is RO and does not have "copy" component: Send empty
                                        subj.next([]); // Notify the subscriber
                                        return Observable.of([]);
                                    });
                            } else {
                                return getComponents();
                            }
                        });
                } else { // Tenant is not present
                    return Observable.of([]);
                }
            });
    }

    // This method can be used for other porpuses besides HM related requests
    checkIsRO(app: ITCIApplication): Observable<boolean> {
        return Observable.combineLatest(
            this.userSvc.getUserInfo(),
            this.userSvc.getAuthForFunction(APPLICATION_SCOPE_TYPES.APP),
            this.evaluateROApp(app),
            (usrInfo, userPath, appRO) => ({ usrInfo, userPath, appRO })
        ).map(res => {
            if (res.appRO) {
                return true;
            } else if (res.userPath.role === USER_ROLES.ADM) {
                return false;
            } else if (res.userPath.role === USER_ROLES.RO) {
                return true;
            } else if (app.owner) { // Scribe do not have this attribute
                return res.usrInfo.uname !== app.owner;
            } else {
                return true;
            }
        });
    }

    private getCanLoadFunctionInput(app: ITCIApplication, mode: TCI_COMPONENT_MODE): BaseAppDetailMenuComponentInput {
        return new BaseAppDetailMenuComponentInput(
            app.type,
            null,
            app,
            this.userSvc,
            this.featureToggleSvc,
            mode,
            undefined,
            null
        );
    }

    /**
     * "TCI_APP_DETAIL_APP_READ_ONLY" is a special selector with the only porpuse of telling
     * if an application is RO. We use the "canLoad" functionality to get the boolean value.
     */
    private evaluateROApp(app: ITCIApplication): Observable<boolean> {
        // Set selector and preload function input
        const selector = TCITenantSelectorType.TCI_APP_DETAIL_APP_READ_ONLY;
        const input = this.getCanLoadFunctionInput(app, TCI_COMPONENT_MODE.COMP_MODE_APP_DETAIL_HEADER);

        const tenantType = app.type;
        const tenantsMap = this.tenantModelSvc.getTenants()[tenantType.toString()];
        const tenantPreloadMap = tenantsMap && tenantsMap.getTenantComponentsPreloadMap() || null;

        if (!tenantPreloadMap) {
            console.log("Preload map not available for", tenantType.toString());
            return Observable.of(false);
        }

        // Store the preloadMap info
        let preloadFn: CanLoadComponentFunction<any, any, any>;
        if (tenantPreloadMap && Object.keys(tenantPreloadMap).length > 0) {
            preloadFn = tenantPreloadMap[selector.toString()] && tenantPreloadMap[selector.toString()].canLoadComponentFn;
        }

        if (preloadFn) {
            const tenantService = this.tenantModelSvc.getTenants()[tenantType.toString()] as AbstractTciTenant;
            const resolvedProviders = ReflectiveInjector.resolve([
                { provide: Injector, useValue: this.injector },
                { provide: TciTenantModelService, useValue: this.tenantModelSvc }
            ]);
            const serviceInjector = ReflectiveInjector.fromResolvedProviders(resolvedProviders, tenantService.getInjector());

            const canLoad = preloadFn(null, input, serviceInjector || this.injector);

            if (typeof canLoad === "boolean") {
                return Observable.of(canLoad);
            } else if ((canLoad as Observable<boolean>).subscribe) {
                return canLoad;
            } else { // canLoad function is not valid
                return Observable.of(false);
            }
        } else { // This component canLoad function is not present
            return Observable.of(false);
        }
    }
}
